import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  format,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  isSameDay,
  isSameMonth
} from 'date-fns';
import { Truck, Plus, Edit2, Trash2, Calendar, AlertCircle, CheckCircle, X, Save, Loader, RefreshCw, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import FleetLiveTracking from './FleetLiveTracking';

const FleetManagement = ({ onBack }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [calendarView, setCalendarView] = useState('week');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [selectedVehicleFilter, setSelectedVehicleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('vehicles'); // 'vehicles' or 'calendar'
  const [disabledVehicles, setDisabledVehicles] = useState(new Set()); // Set von deaktivierten Fahrzeug-IDs
  const [formData, setFormData] = useState({
    license_plate: '',
    stressfrei_id: '',
    traccar_device_id: '',
    traccar_unique_id: '',
    vehicle_type: '',
    brand: '',
    model: '',
    year: '',
    mileage: '',
    max_load_capacity: '',
    max_volume: '',
    length: '',
    width: '',
    height: '',
    fuel_type: '',
    insurance_number: '',
    insurance_expiry: '',
    next_inspection_date: '',
    last_inspection_date: '',
    registration_date: '',
    notes: '',
    status: 'active'
  });

  const SERVICEPROVIDER_API_URL =
    process.env.REACT_APP_SERVICEPROVIDER_API_URL ||
    'https://lolworlds.online/api/serviceprovider/getServiceprovider';
  const SFS_API_TOKEN = process.env.REACT_APP_SFS_API_TOKEN;

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    if (cleanBaseUrl.endsWith('/api')) {
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  // Load vehicles
  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Nicht authentifiziert');
        setLoading(false);
        return;
      }

      const url = createApiUrl('api/vehicles');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Fahrzeuge');
      }

      const data = await response.json();
      setVehicles(data || []);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredCalendarEvents = useMemo(() => {
    let filtered = calendarEvents;
    
    // Filter nach ausgewähltem Fahrzeug (Dropdown)
    if (selectedVehicleFilter !== 'all') {
      filtered = filtered.filter(event => event.vehicleId === selectedVehicleFilter);
    }
    
    // Filter nach deaktivierten Fahrzeugen (aus Farblegende)
    filtered = filtered.filter(event => {
      if (event.type === 'inspection') return true; // HU immer anzeigen
      return !disabledVehicles.has(event.vehicleId);
    });
    
    return filtered;
  }, [calendarEvents, selectedVehicleFilter, disabledVehicles]);

  const calendarVehicleOptions = useMemo(() => {
    const map = new Map();
    calendarEvents.forEach(event => {
      if (!event.vehicleId) return;
      if (!map.has(event.vehicleId)) {
        map.set(event.vehicleId, event.vehicleName || event.vehicleId);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [calendarEvents]);

  const vehicleFilterOptions = useMemo(() => {
    if (calendarVehicleOptions.length > 0) {
      return calendarVehicleOptions;
    }
    const map = new Map();
    (vehicles || []).forEach(vehicle => {
      const id = vehicle.stressfrei_id || vehicle.id;
      const name = vehicle.license_plate || vehicle.vehicle_type || 'Fahrzeug';
      if (!map.has(id)) {
        map.set(id, name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [calendarVehicleOptions, vehicles]);

  const eventsByDate = useMemo(() => {
    const grouped = {};
    filteredCalendarEvents.forEach(event => {
      if (!event.start) return;
      const key = format(event.start, 'yyyy-MM-dd');
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(event);
    });
    Object.values(grouped).forEach(list => {
      list.sort((a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0));
    });
    return grouped;
  }, [filteredCalendarEvents]);

  const getEventsForDate = (date) => {
    const key = format(date, 'yyyy-MM-dd');
    return eventsByDate[key] || [];
  };

  const resetForm = () => {
    setFormData({
      license_plate: '',
      stressfrei_id: '',
      traccar_device_id: '',
      traccar_unique_id: '',
      vehicle_type: '',
      brand: '',
      model: '',
      year: '',
      mileage: '',
      max_load_capacity: '',
      max_volume: '',
      length: '',
      width: '',
      height: '',
      fuel_type: '',
      insurance_number: '',
      insurance_expiry: '',
      next_inspection_date: '',
      last_inspection_date: '',
      registration_date: '',
      notes: '',
      status: 'active'
    });
    setEditingVehicle(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingVehicle 
        ? createApiUrl(`api/vehicles/${editingVehicle.id}`)
        : createApiUrl('api/vehicles');

      const method = editingVehicle ? 'PUT' : 'POST';
      
      // Prepare data - convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        license_plate: formData.license_plate?.trim() || null,
        stressfrei_id: formData.stressfrei_id?.trim() || null,
        traccar_device_id: formData.traccar_device_id?.trim() ? parseInt(formData.traccar_device_id, 10) : null,
        traccar_unique_id: formData.traccar_unique_id?.trim() || null,
        year: formData.year ? parseInt(formData.year) : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : 0,
        max_load_capacity: formData.max_load_capacity ? parseFloat(formData.max_load_capacity) : null,
        max_volume: formData.max_volume ? parseFloat(formData.max_volume) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        insurance_expiry: formData.insurance_expiry || null,
        last_inspection_date: formData.last_inspection_date || null,
        registration_date: formData.registration_date || null,
        notes: formData.notes || null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Speichern');
      }

      await loadVehicles();
      resetForm();
    } catch (err) {
      console.error('Error saving vehicle:', err);
      alert(err.message || 'Fehler beim Speichern des Fahrzeugs');
    }
  };

  const handleCalendarPrev = () => {
    setCalendarDate(prev => {
      if (calendarView === 'day') return subDays(prev, 1);
      if (calendarView === 'week') return subWeeks(prev, 1);
      return subMonths(prev, 1);
    });
  };

  const handleCalendarNext = () => {
    setCalendarDate(prev => {
      if (calendarView === 'day') return addDays(prev, 1);
      if (calendarView === 'week') return addWeeks(prev, 1);
      return addMonths(prev, 1);
    });
  };

  const handleCalendarToday = () => {
    setCalendarDate(new Date());
  };

  const normalizeLicensePlate = (value) => {
    if (!value) return null;
    return value.toString().trim().replace(/\s+/g, ' ').toUpperCase();
  };

  const findLicenseInObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null;

    for (const [key, val] of Object.entries(obj)) {
      if (!key || val == null) continue;
      const normalizedKey = key.toLowerCase().replace(/[\s_-]+/g, '');
      const isLicenseKey =
        normalizedKey.includes('kennzeichen') ||
        normalizedKey.includes('licenseplate') ||
        normalizedKey.includes('license');

      if (isLicenseKey && typeof val === 'string' && val.trim()) {
        return val;
      }
    }

    return null;
  };

  const buildVehicleDisplayName = (entity = {}, props = {}) => {
    // Priorität: Vor- und Nachname zuerst, dann andere Felder
    const vornameNachname = [entity.Vorname, entity.Nachname].filter(Boolean).join(' ').trim();
    
    const candidates = [
      vornameNachname, // Vor- und Nachname haben höchste Priorität
      entity.name,
      entity.Name,
      props.vehicle_name,
      props.VehicleName,
      props.fahrzeugname,
      entity.Nummer,
      props.fahrzeugnummer
    ].filter(Boolean);

    const displayName = candidates.find(name => typeof name === 'string' && name.trim().length > 0);
    return displayName ? displayName.trim() : null;
  };

  const getVehicleNameFromItem = (item) => {
    const entity = item?.Teammitglied || item || {};
    const props = item?.Eigenschaften || entity?.Eigenschaften || {};
    return (
      buildVehicleDisplayName(entity, props) ||
      entity.Nummer ||
      entity.Id ||
      'Unbekanntes Fahrzeug'
    );
  };

  const extractVehicleFromStressfrei = (item) => {
    console.log('🔍 [FleetManagement] Extracting vehicle from item:', item);
    
    const entity = item?.Teammitglied || item || {};
    const props = item?.Eigenschaften || entity?.Eigenschaften || {};
    const displayName = buildVehicleDisplayName(entity, props);
    
    console.log('🔍 [FleetManagement] Entity:', entity);
    console.log('🔍 [FleetManagement] Eigenschaften:', props);
    
    const stressfreiId = entity.Id || entity.id || entity.personalid || entity.uuid || props.id || props.personalid;
    console.log('🔍 [FleetManagement] Extracted stressfrei_id:', stressfreiId);
    
    const dynamicPropsLicense = findLicenseInObject(props);
    const dynamicEntityLicense = findLicenseInObject(entity);

    // Nur echte Kennzeichen-Felder suchen, NICHT Nummer, Name, etc.
    const licenseCandidates = [
      props.kennzeichen,
      props.Kennzeichen,
      props.vehicle_license_plate,
      props.vehicle_licenseplate,
      props.vehicleKennzeichen,
      props.vehicleLicensePlate,
      props.vehicleLicense,
      props.vehicle_license,
      entity.kennzeichen,
      entity.Kennzeichen,
      entity.license_plate,
      entity.licensePlate,
      entity.LicensePlate,
      entity.License,
      // KEINE Nummer, Name, etc. - diese sind keine Kennzeichen!
      dynamicPropsLicense,
      dynamicEntityLicense
    ];
    
    console.log('🔍 [FleetManagement] License plate candidates:', licenseCandidates);
    console.log('🔍 [FleetManagement] Display name (Vor- und Nachname):', displayName);
    
    let licensePlate = normalizeLicensePlate(licenseCandidates.find(Boolean));
    console.log('🔍 [FleetManagement] Normalized license plate:', licensePlate);
    
    // Wenn kein Kennzeichen gefunden wurde, verwende den Vor- und Nachnamen
    if (!licensePlate && displayName) {
      licensePlate = displayName;
      console.log('ℹ️ [FleetManagement] Using display name (Vor- und Nachname) as license plate:', licensePlate);
    }

    // Nur als allerletzter Fallback die ID verwenden
    if (!licensePlate && stressfreiId) {
      licensePlate = `Stressfrei-${stressfreiId}`;
      console.log('ℹ️ [FleetManagement] Using stressfrei ID as last fallback:', licensePlate);
    }
    
    if (!stressfreiId || !licensePlate) {
      console.warn('⚠️ [FleetManagement] Missing data - stressfreiId:', stressfreiId, 'licensePlate:', licensePlate);
      return null;
    }

    const vehicleType =
      props.vehicle_type ||
      props.VehicleType ||
      props.fahrzeugtyp ||
      props.fahrzeugart ||
      'Unbekannt';

    const result = {
      stressfrei_id: String(stressfreiId),
      license_plate: licensePlate,
      vehicle_type: vehicleType || 'Unbekannt',
      vehicle_name: displayName || licensePlate
    };
    
    console.log('✅ [FleetManagement] Extracted vehicle:', result);
    return result;
  };

  const parseStressfreiDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const normalizedDate = dateStr.toString().split('T')[0];
    let normalizedTime = (timeStr || '00:00:00').toString().trim();
    if (normalizedTime.length === 5) {
      normalizedTime = `${normalizedTime}:00`;
    }
    const isoString = `${normalizedDate}T${normalizedTime}`;
    const parsed = new Date(isoString);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const calculateViewRange = useCallback((date, view) => {
    switch (view) {
      case 'day':
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
      case 'week': {
        const start = startOfWeek(date, { weekStartsOn: 1 });
        const end = endOfWeek(date, { weekStartsOn: 1 });
        return { start, end };
      }
      case 'month':
      default: {
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        const start = startOfWeek(monthStart, { weekStartsOn: 1 });
        const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
        return { start, end };
      }
    }
  }, []);

  const fetchStressfreiVehicles = async () => {
    const today = new Date();
    const endDate = addDays(today, 7);
    const dateFrom = format(today, 'yyyy-MM-dd');
    const dateTo = format(endDate, 'yyyy-MM-dd');

    const requestBody = {
      date_from: dateFrom,
      date_to: dateTo,
      joins: ['Vertraege', 'Eigenschaften', 'Termine']
    };

    console.log('📥 [FleetManagement] Fetching vehicles from Stressfrei with requestBody:', requestBody);

    const response = await axios.post(SERVICEPROVIDER_API_URL, requestBody, {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: SFS_API_TOKEN
      }
    });

    console.log('📦 [FleetManagement] Raw response data:', response.data);
    console.log('📦 [FleetManagement] Response data type:', typeof response.data);
    console.log('📦 [FleetManagement] Is array:', Array.isArray(response.data));

    if (!response.data || !Array.isArray(response.data)) {
      console.warn('⚠️ [FleetManagement] Response is not an array');
      return [];
    }

    console.log('📊 [FleetManagement] Total items in response:', response.data.length);

    const filtered = response.data.filter(item => {
      const eigenschaften = item?.Eigenschaften || {};
      const isVehicle = eigenschaften['staff_status_Fuhrpark_Maschinen '] === null;
      console.log('🔍 [FleetManagement] Item check:', {
        hasEigenschaften: !!item?.Eigenschaften,
        staff_status: eigenschaften['staff_status_Fuhrpark_Maschinen '],
        isVehicle
      });
      return isVehicle;
    });

    console.log('✅ [FleetManagement] Filtered vehicles count:', filtered.length);
    return filtered;
  };

  const fetchCalendarEvents = useCallback(async () => {
    if (!SFS_API_TOKEN) {
      setCalendarEvents([]);
      return;
    }

    setCalendarLoading(true);
    setCalendarError(null);

    try {
      const { start, end } = calculateViewRange(calendarDate, calendarView);
      const dateFrom = format(start, 'yyyy-MM-dd');
      const dateTo = format(end, 'yyyy-MM-dd');

      const requestBody = {
        date_from: dateFrom,
        date_to: dateTo,
        joins: ['Vertraege', 'Eigenschaften', 'Termine']
      };

      const response = await axios.post(SERVICEPROVIDER_API_URL, requestBody, {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: SFS_API_TOKEN
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      const vehicleItems = data.filter(item => {
        const eigenschaften = item?.Eigenschaften || {};
        return eigenschaften['staff_status_Fuhrpark_Maschinen '] === null;
      });

      const stressfreiEvents = [];

      vehicleItems.forEach(item => {
        const vehicleName = getVehicleNameFromItem(item);
        const entity = item?.Teammitglied || item || {};
        const vehicleId =
          entity.Id ||
          entity.id ||
          entity.personalid ||
          entity.uuid ||
          entity.Nummer ||
          vehicleName;

        const termine = item?.Termine || item?.termine || [];

        termine.forEach((termin, index) => {
          const terminDatum = termin?.datum || termin?.Datum;
          const startzeit = termin?.startzeit || termin?.Startzeit;
          const endzeit = termin?.endzeit || termin?.Endzeit;

          const eventStart = parseStressfreiDateTime(terminDatum, startzeit);
          if (!eventStart) {
            return;
          }

          const eventEnd = parseStressfreiDateTime(terminDatum, endzeit) || eventStart;

          // Extrahiere aussagekräftigen Titel aus verschiedenen Feldern
          const terminTitle = 
            termin?.kommentar || 
            termin?.Kommentar || 
            termin?.titel || 
            termin?.Titel || 
            termin?.vorgangsno || 
            termin?.Vorgangsno ||
            termin?.angebotsno ||
            termin?.Angebotsno ||
            termin?.terminart ||
            termin?.Terminart ||
            'Stressfrei-Termin';

          stressfreiEvents.push({
            id: `${vehicleId}-appointment-${index}-${eventStart.getTime()}`,
            vehicleId: String(vehicleId),
            vehicleName,
            title: terminTitle,
            start: eventStart,
            end: eventEnd,
            allDay: false,
            type: 'appointment',
            meta: {
              description: termin?.kommentar || termin?.Kommentar || terminTitle,
              source: 'stressfrei'
            }
          });
        });
      });

      const inspectionEvents = (vehicles || [])
        .filter(vehicle => vehicle.next_inspection_date)
        .map(vehicle => {
          const inspectionDate = new Date(vehicle.next_inspection_date);
          if (isNaN(inspectionDate.getTime())) {
            return null;
          }

          return {
            id: `inspection-${vehicle.id}`,
            vehicleId: vehicle.stressfrei_id || vehicle.id || vehicle.license_plate || 'local',
            vehicleName: vehicle.license_plate || vehicle.vehicle_type || 'Fahrzeug',
            title: 'Nächste Hauptuntersuchung',
            start: inspectionDate,
            end: inspectionDate,
            allDay: true,
            type: 'inspection',
            meta: {
              description: `HU fällig am ${format(inspectionDate, 'dd.MM.yyyy')}`,
              source: 'inspection'
            }
          };
        })
        .filter(Boolean);

      const combinedEvents = [...stressfreiEvents, ...inspectionEvents].sort((a, b) => {
        const startA = a.start ? a.start.getTime() : 0;
        const startB = b.start ? b.start.getTime() : 0;
        return startA - startB;
      });

      setCalendarEvents(combinedEvents);
    } catch (err) {
      console.error('Error loading calendar events:', err);
      setCalendarError(err.message || 'Fehler beim Laden der Kalenderdaten');
    } finally {
      setCalendarLoading(false);
    }
  }, [SFS_API_TOKEN, SERVICEPROVIDER_API_URL, calendarDate, calendarView, calculateViewRange, vehicles]);

  // Load calendar events when dependencies change
  useEffect(() => {
    fetchCalendarEvents();
  }, [fetchCalendarEvents]);

  const handleSyncWithStressfrei = async () => {
    if (syncing) return;

    if (!SFS_API_TOKEN) {
      alert('Kein Stressfrei API-Token konfiguriert (REACT_APP_SFS_API_TOKEN).');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Nicht authentifiziert');
      return;
    }

    try {
      setSyncing(true);
      setSyncStatus(null);

      const todayStr = new Date().toISOString().split('T')[0];
      const stressfreiVehicles = await fetchStressfreiVehicles();
      
      console.log('🚗 [FleetManagement] Stressfrei vehicles after filtering:', stressfreiVehicles.length);
      
      const remoteVehicles = stressfreiVehicles
        .map(extractVehicleFromStressfrei)
        .filter(Boolean);

      console.log('✅ [FleetManagement] Successfully extracted vehicles:', remoteVehicles.length);
      console.log('📋 [FleetManagement] Extracted vehicles:', remoteVehicles);

      if (remoteVehicles.length === 0) {
        console.warn('⚠️ [FleetManagement] No vehicles extracted - check extraction logic');
        setSyncStatus({
          type: 'info',
          message: 'Keine Fahrzeuge aus Stressfrei gefunden. Bitte Browser-Konsole prüfen (F12).'
        });
        return;
      }

      const existingStressfreiIds = new Set(
        vehicles.map(v => v.stressfrei_id?.toString()).filter(Boolean)
      );
      const existingLicensePlates = new Set(
        vehicles.map(v => normalizeLicensePlate(v.license_plate)).filter(Boolean)
      );
      const usedLicensePlates = new Set(existingLicensePlates);

      const ensureUniqueLicensePlate = (value, stressfreiId) => {
        let candidate = value || (stressfreiId ? `Stressfrei-${stressfreiId}` : `Fahrzeug-${Date.now()}`);
        let normalized = normalizeLicensePlate(candidate);
        let counter = 2;

        while (normalized && usedLicensePlates.has(normalized)) {
          const base = value || (stressfreiId ? `Stressfrei-${stressfreiId}` : 'Fahrzeug');
          candidate = `${base} (${counter})`;
          normalized = normalizeLicensePlate(candidate);
          counter += 1;
        }

        if (normalized) {
          usedLicensePlates.add(normalized);
        }

        return candidate;
      };

      const vehiclesToCreate = remoteVehicles.filter(remote => {
        if (remote.stressfrei_id && existingStressfreiIds.has(remote.stressfrei_id)) {
          return false;
        }

        if (!remote.stressfrei_id) {
          return !existingLicensePlates.has(remote.license_plate);
        }

        return true;
      });

      if (vehiclesToCreate.length === 0) {
        setSyncStatus({
          type: 'info',
          message: 'Alle Stressfrei-Fahrzeuge sind bereits im Fuhrpark angelegt.'
        });
        return;
      }

      const createdVehicles = [];
      for (const remote of vehiclesToCreate) {
        try {
          const uniqueLicensePlate = ensureUniqueLicensePlate(remote.license_plate, remote.stressfrei_id);

          const payload = {
            license_plate: uniqueLicensePlate,
            vehicle_name: remote.vehicle_name || uniqueLicensePlate,
            stressfrei_id: remote.stressfrei_id,
            vehicle_type: remote.vehicle_type || 'Unbekannt',
            next_inspection_date: todayStr,
            status: 'active',
            notes: `Automatisch aus Stressfrei importiert am ${new Date().toLocaleDateString('de-DE')}`
          };

          const url = createApiUrl('api/vehicles');
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Fehler beim Anlegen');
          }
          createdVehicles.push(remote.license_plate);
        } catch (err) {
          console.error('Fehler beim Import eines Fahrzeugs:', err);
        }
      }

      await loadVehicles();

      if (createdVehicles.length === 0) {
        setSyncStatus({
          type: 'error',
          message:
            'Import fehlgeschlagen. Details finden Sie in der Browser-Konsole.'
        });
        return;
      }

      setSyncStatus({
        type: 'success',
        message: `${createdVehicles.length} Fahrzeug(e) aus Stressfrei importiert.`
      });
    } catch (err) {
      console.error('Error syncing vehicles from Stressfrei:', err);
      setSyncStatus({
        type: 'error',
        message: err.message || 'Unbekannter Fehler beim Sync mit Stressfrei'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      license_plate: vehicle.license_plate || '',
      stressfrei_id: vehicle.stressfrei_id || '',
      traccar_device_id: vehicle.traccar_device_id != null ? String(vehicle.traccar_device_id) : '',
      traccar_unique_id: vehicle.traccar_unique_id || '',
      vehicle_type: vehicle.vehicle_type || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      mileage: vehicle.mileage || '',
      max_load_capacity: vehicle.max_load_capacity || '',
      max_volume: vehicle.max_volume || '',
      length: vehicle.length || '',
      width: vehicle.width || '',
      height: vehicle.height || '',
      fuel_type: vehicle.fuel_type || '',
      insurance_number: vehicle.insurance_number || '',
      insurance_expiry: vehicle.insurance_expiry || '',
      next_inspection_date: vehicle.next_inspection_date || '',
      last_inspection_date: vehicle.last_inspection_date || '',
      registration_date: vehicle.registration_date || '',
      notes: vehicle.notes || '',
      status: vehicle.status || 'active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie dieses Fahrzeug wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = createApiUrl(`api/vehicles/${id}`);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      await loadVehicles();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      alert('Fehler beim Löschen des Fahrzeugs');
    }
  };

  // Generiere konsistente Farbe für jedes Fahrzeug basierend auf ID/Name
  const getVehicleColor = (vehicleId, vehicleName) => {
    const identifier = String(vehicleId || vehicleName || 'default');
    
    // Hash-Funktion für konsistente Farbzuweisung
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Farbpalette mit unterschiedlichen, gut unterscheidbaren Farben
    // Enthält alle benötigten Varianten für Badges und Day-View
    const colors = [
      { 
        bg: 'bg-blue-100', 
        text: 'text-blue-900', 
        border: 'border-blue-200',
        borderL: 'border-l-blue-400',
        bgLight: 'bg-blue-50'
      },
      { 
        bg: 'bg-indigo-100', 
        text: 'text-indigo-900', 
        border: 'border-indigo-200',
        borderL: 'border-l-indigo-400',
        bgLight: 'bg-indigo-50'
      },
      { 
        bg: 'bg-purple-100', 
        text: 'text-purple-900', 
        border: 'border-purple-200',
        borderL: 'border-l-purple-400',
        bgLight: 'bg-purple-50'
      },
      { 
        bg: 'bg-pink-100', 
        text: 'text-pink-900', 
        border: 'border-pink-200',
        borderL: 'border-l-pink-400',
        bgLight: 'bg-pink-50'
      },
      { 
        bg: 'bg-red-100', 
        text: 'text-red-900', 
        border: 'border-red-200',
        borderL: 'border-l-red-400',
        bgLight: 'bg-red-50'
      },
      { 
        bg: 'bg-orange-100', 
        text: 'text-orange-900', 
        border: 'border-orange-200',
        borderL: 'border-l-orange-400',
        bgLight: 'bg-orange-50'
      },
      { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-900', 
        border: 'border-yellow-200',
        borderL: 'border-l-yellow-400',
        bgLight: 'bg-yellow-50'
      },
      { 
        bg: 'bg-green-100', 
        text: 'text-green-900', 
        border: 'border-green-200',
        borderL: 'border-l-green-400',
        bgLight: 'bg-green-50'
      },
      { 
        bg: 'bg-teal-100', 
        text: 'text-teal-900', 
        border: 'border-teal-200',
        borderL: 'border-l-teal-400',
        bgLight: 'bg-teal-50'
      },
      { 
        bg: 'bg-cyan-100', 
        text: 'text-cyan-900', 
        border: 'border-cyan-200',
        borderL: 'border-l-cyan-400',
        bgLight: 'bg-cyan-50'
      },
      { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-900', 
        border: 'border-emerald-200',
        borderL: 'border-l-emerald-400',
        bgLight: 'bg-emerald-50'
      },
      { 
        bg: 'bg-lime-100', 
        text: 'text-lime-900', 
        border: 'border-lime-200',
        borderL: 'border-l-lime-400',
        bgLight: 'bg-lime-50'
      },
    ];
    
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };

  const renderEventBadge = (event, compact = false) => {
    // Für Hauptuntersuchungen immer Amber verwenden
    if (event.type === 'inspection') {
      const baseClasses = 'bg-amber-100 text-amber-900 border border-amber-200';
      return (
        <div key={`${event.id}-${compact ? 'compact' : 'full'}`} className={`rounded-md px-2 py-1 ${compact ? 'text-[11px]' : 'text-xs'} ${baseClasses}`}>
          <div className="font-semibold truncate">{compact ? event.title : `${event.title} • ${event.vehicleName}`}</div>
          {!compact && (
            <div className="text-[11px] text-gray-600">
              {event.allDay ? 'Ganztägig' : `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`}
            </div>
          )}
        </div>
      );
    }
    
    // Für Stressfrei-Termine: Fahrzeug-spezifische Farbe
    const vehicleColor = getVehicleColor(event.vehicleId, event.vehicleName);
    const baseClasses = `${vehicleColor.bg} ${vehicleColor.text} border ${vehicleColor.border}`;

    return (
      <div key={`${event.id}-${compact ? 'compact' : 'full'}`} className={`rounded-md px-2 py-1 ${compact ? 'text-[11px]' : 'text-xs'} ${baseClasses}`}>
        <div className="font-semibold truncate">{compact ? event.title : `${event.title} • ${event.vehicleName}`}</div>
        {!compact && (
          <div className="text-[11px] text-gray-600">
            {event.allDay ? 'Ganztägig' : `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`}
          </div>
        )}
      </div>
    );
  };

  const handleDayClick = (day) => {
    setCalendarDate(day);
    setCalendarView('day');
  };

  const toggleVehicleVisibility = (vehicleId) => {
    setDisabledVehicles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  const renderMonthView = () => {
    const { start, end } = calculateViewRange(calendarDate, 'month');
    const days = eachDayOfInterval({ start, end });
    const weeks = [];

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="space-y-3">
        {weeks.map((week, index) => (
          <div key={`week-${index}`} className="grid grid-cols-1 sm:grid-cols-7 gap-2">
            {week.map((day) => {
              const events = getEventsForDate(day);
              const isMuted = !isSameMonth(day, calendarDate);
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  className={`rounded-lg border p-2 min-h-[130px] cursor-pointer transition-all hover:shadow-md hover:border-blue-300 ${
                    isMuted ? 'bg-gray-50 text-gray-400' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs font-semibold mb-1">
                    <span>{format(day, 'EEE')}</span>
                    <span>{format(day, 'dd.MM.')}</span>
                  </div>
                  <div className="space-y-1">
                    {events.length === 0 ? (
                      <div className="text-[11px] text-gray-400">-</div>
                    ) : (
                      <>
                        {events.slice(0, 3).map(event => renderEventBadge(event, true))}
                        {events.length > 3 && (
                          <div className="text-[11px] text-gray-500">+{events.length - 3} weitere</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderWeekView = () => {
    const { start, end } = calculateViewRange(calendarDate, 'week');
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {days.map(day => {
          const events = getEventsForDate(day);
          return (
            <div 
              key={day.toISOString()} 
              onClick={() => handleDayClick(day)}
              className="border rounded-lg p-3 bg-white min-h-[200px] cursor-pointer transition-all hover:shadow-md hover:border-blue-300"
            >
              <div className="flex items-center justify-between text-sm font-semibold mb-2">
                <span>{format(day, 'EEE')}</span>
                <span>{format(day, 'dd.MM.')}</span>
              </div>
              <div className="space-y-2">
                {events.length === 0 ? (
                  <div className="text-xs text-gray-400">Keine Termine</div>
                ) : (
                  events.map(event => renderEventBadge(event))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const events = getEventsForDate(calendarDate);
    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="text-lg font-semibold mb-3">
          {format(calendarDate, 'EEEE, dd.MM.yyyy')}
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-gray-500">Keine Termine an diesem Tag.</div>
        ) : (
          <div className="space-y-3">
            {events.map(event => {
              // Für Hauptuntersuchungen immer Amber verwenden
              let borderColor = 'border-l-amber-400';
              let bgColor = 'bg-amber-50';
              
              if (event.type !== 'inspection') {
                // Für Stressfrei-Termine: Fahrzeug-spezifische Farbe
                const vehicleColor = getVehicleColor(event.vehicleId, event.vehicleName);
                borderColor = vehicleColor.borderL;
                bgColor = vehicleColor.bgLight;
              }
              
              return (
                <div
                  key={event.id}
                  className={`rounded-lg border-l-4 px-3 py-2 ${borderColor} ${bgColor}`}
                >
                  <div className="text-sm font-semibold">{event.title}</div>
                  <div className="text-xs text-gray-600">{event.vehicleName}</div>
                  <div className="text-xs text-gray-500">
                    {event.allDay ? 'Ganztägig' : `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`}
                  </div>
                  {event.meta?.description && (
                    <div className="text-xs text-gray-600 mt-1">{event.meta.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCalendarBody = () => {
    switch (calendarView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
      default:
        return renderMonthView();
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysUntilInspection = (inspectionDate) => {
    if (!inspectionDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inspection = new Date(inspectionDate);
    inspection.setHours(0, 0, 0, 0);
    const diffTime = inspection - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', label: 'Aktiv' },
      maintenance: { color: 'bg-yellow-100 text-yellow-800', label: 'Wartung' },
      out_of_service: { color: 'bg-red-100 text-red-800', label: 'Außer Betrieb' }
    };
    const badge = badges[status] || badges.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Lade Fahrzeuge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Fehler</h2>
          <p className="text-gray-600">{error}</p>
          {onBack && (
            <button
              onClick={onBack}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Zurück
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {syncStatus && (
          <div
            className={`mb-4 sm:mb-6 rounded-lg border px-4 py-3 flex items-start gap-3 ${
              syncStatus.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-900'
                : syncStatus.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-900'
                : 'bg-blue-50 border-blue-200 text-blue-900'
            }`}
          >
            {syncStatus.type === 'success' && (
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
            )}
            {syncStatus.type === 'error' && (
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
            )}
            {syncStatus.type === 'info' && (
              <Calendar className="h-5 w-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{syncStatus.message}</span>
          </div>
        )}
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Fuhrparkmanagement</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Verwalten Sie Ihre Fahrzeugflotte
              </p>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <button
                onClick={handleSyncWithStressfrei}
                disabled={syncing}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {syncing ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Stressfrei Sync
              </button>
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  ← Zurück
                </button>
              )}
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Neues Fahrzeug
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('vehicles')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'vehicles'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Truck className="h-4 w-4 inline-block mr-2" />
                Fahrzeugübersicht
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'calendar'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calendar className="h-4 w-4 inline-block mr-2" />
                Kalender
              </button>
              <button
                onClick={() => setActiveTab('live')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'live'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MapPin className="h-4 w-4 inline-block mr-2" />
                Live-Standort
              </button>
            </nav>
          </div>
        </div>

        {/* Live-Standort Tab */}
        {activeTab === 'live' && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <FleetLiveTracking />
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-600" />
                Fahrzeugkalender
              </h2>
              <p className="text-sm text-gray-500">
                Termine und nächste Hauptuntersuchungen im Überblick
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <div className="flex items-center gap-2">
                <select
                  value={calendarView}
                  onChange={(e) => setCalendarView(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="day">Tag</option>
                  <option value="week">Woche</option>
                  <option value="month">Monat</option>
                </select>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={handleCalendarPrev}
                    className="p-2 hover:bg-gray-50 transition-colors"
                    title="Zurück"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCalendarToday}
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Heute
                  </button>
                  <button
                    onClick={handleCalendarNext}
                    className="p-2 hover:bg-gray-50 transition-colors"
                    title="Weiter"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <select
                value={selectedVehicleFilter}
                onChange={(e) => setSelectedVehicleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">Alle Fahrzeuge</option>
                {vehicleFilterOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {calendarError && (
            <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
              <AlertCircle className="h-4 w-4" />
              {calendarError}
            </div>
          )}

          {/* Legende */}
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-200 border border-amber-400" />
                Nächste Hauptuntersuchung
              </span>
            </div>
            
            {/* Fahrzeug-Farbzuordnung */}
            {(() => {
              const vehicleMap = new Map();
              calendarEvents.forEach(event => {
                if (event.type === 'inspection') return; // HU ignorieren
                const key = event.vehicleId || event.vehicleName;
                if (key && !vehicleMap.has(key)) {
                  vehicleMap.set(key, {
                    id: event.vehicleId,
                    name: event.vehicleName || event.vehicleId,
                    color: getVehicleColor(event.vehicleId, event.vehicleName)
                  });
                }
              });
              
              const vehicleList = Array.from(vehicleMap.values());
              
              if (vehicleList.length === 0) {
                return null;
              }
              
              const allDisabled = vehicleList.every(v => disabledVehicles.has(v.id));
              const someDisabled = vehicleList.some(v => disabledVehicles.has(v.id));

              return (
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-700">
                      Fahrzeug-Farbzuordnung (klicken zum Ein-/Ausblenden):
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          if (allDisabled) {
                            setDisabledVehicles(new Set());
                          } else {
                            const allIds = new Set(vehicleList.map(v => v.id));
                            setDisabledVehicles(allIds);
                          }
                        }}
                        className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                        title={allDisabled ? 'Alle einblenden' : 'Alle ausblenden'}
                      >
                        {allDisabled ? 'Alle ein' : 'Alle aus'}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {vehicleList.map((vehicle, index) => {
                      const isDisabled = disabledVehicles.has(vehicle.id);
                      return (
                        <button
                          key={`${vehicle.id}-${index}`}
                          onClick={() => toggleVehicleVisibility(vehicle.id)}
                          className={`flex items-center gap-2 px-2 py-1 rounded-md border transition-all cursor-pointer hover:shadow-md ${
                            isDisabled 
                              ? `opacity-40 line-through ${vehicle.color.bg} ${vehicle.color.border} border-gray-300` 
                              : `${vehicle.color.bg} ${vehicle.color.border} border hover:scale-105`
                          }`}
                          title={isDisabled ? 'Klicken zum Einblenden' : 'Klicken zum Ausblenden'}
                        >
                          <span className={`w-3 h-3 rounded-full ${vehicle.color.bg} ${vehicle.color.border} border`} />
                          <span className={`text-xs font-medium ${vehicle.color.text}`}>{vehicle.name}</span>
                          {isDisabled && (
                            <X className="h-3 w-3 text-gray-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="mt-4">
            {calendarLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                <Loader className="h-5 w-5 animate-spin mr-2" />
                Kalender wird geladen...
              </div>
            ) : (
              renderCalendarBody()
            )}
          </div>
        </div>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
        <>
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}
                </h2>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Required Fields */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kennzeichen (optional)
                    </label>
                    <input
                      type="text"
                      name="license_plate"
                      value={formData.license_plate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stressfrei ID
                    </label>
                    <input
                      type="text"
                      name="stressfrei_id"
                      value={formData.stressfrei_id}
                      onChange={handleInputChange}
                      placeholder="z. B. FZG-123 oder UUID aus Stressfrei"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Traccar-Kennung (Live-Standort)
                    </label>
                    <input
                      type="text"
                      name="traccar_unique_id"
                      value={formData.traccar_unique_id}
                      onChange={handleInputChange}
                      placeholder="z. B. UCFLFT1H (Kennung aus Traccar → Erforderlich)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Einfach die <strong>Kennung</strong> aus dem Traccar-Gerät (z. B. UCFLFT1H) eintragen – verbindet mit dem Live-Standort.</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Traccar-Gerät-ID (optional)
                    </label>
                    <input
                      type="number"
                      name="traccar_device_id"
                      value={formData.traccar_device_id}
                      onChange={handleInputChange}
                      placeholder="z. B. 5 (in URL: …/device/5)"
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Falls bekannt: interne Traccar-ID (steht in der Gerät-URL). Kennung reicht in der Regel.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fahrzeugtyp <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="vehicle_type"
                      value={formData.vehicle_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Transporter">Transporter</option>
                      <option value="LKW">LKW</option>
                      <option value="Sprinter">Sprinter</option>
                      <option value="Kastenwagen">Kastenwagen</option>
                      <option value="Pritschenwagen">Pritschenwagen</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="active">Aktiv</option>
                      <option value="maintenance">Wartung</option>
                      <option value="out_of_service">Außer Betrieb</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marke</label>
                    <input
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modell</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Baujahr</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kilometerstand</label>
                    <input
                      type="number"
                      name="mileage"
                      value={formData.mileage}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max. Ladekapazität (kg)</label>
                    <input
                      type="number"
                      name="max_load_capacity"
                      value={formData.max_load_capacity}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max. Volumen (m³)</label>
                    <input
                      type="number"
                      name="max_volume"
                      value={formData.max_volume}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Länge (m)</label>
                    <input
                      type="number"
                      name="length"
                      value={formData.length}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Breite (m)</label>
                    <input
                      type="number"
                      name="width"
                      value={formData.width}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Höhe (m)</label>
                    <input
                      type="number"
                      name="height"
                      value={formData.height}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kraftstoffart</label>
                    <select
                      name="fuel_type"
                      value={formData.fuel_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Benzin">Benzin</option>
                      <option value="Elektro">Elektro</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="LPG">LPG</option>
                      <option value="CNG">CNG</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Versicherungsnummer</label>
                    <input
                      type="text"
                      name="insurance_number"
                      value={formData.insurance_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Versicherung abläuft</label>
                    <input
                      type="date"
                      name="insurance_expiry"
                      value={formData.insurance_expiry}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nächste Hauptuntersuchung <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="next_inspection_date"
                      value={formData.next_inspection_date}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Letzte Hauptuntersuchung</label>
                    <input
                      type="date"
                      name="last_inspection_date"
                      value={formData.last_inspection_date}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zulassungsdatum</label>
                    <input
                      type="date"
                      name="registration_date"
                      value={formData.registration_date}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Vehicles List */}
        {vehicles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <Truck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Fahrzeuge vorhanden</h3>
            <p className="text-gray-600 mb-4">Fügen Sie Ihr erstes Fahrzeug hinzu, um zu beginnen.</p>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              Neues Fahrzeug
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {vehicles.map((vehicle) => {
              const daysUntilInspection = getDaysUntilInspection(vehicle.next_inspection_date);
              const isInspectionSoon = daysUntilInspection !== null && daysUntilInspection <= 30;
              const isInspectionOverdue = daysUntilInspection !== null && daysUntilInspection < 0;

              return (
                <div
                  key={vehicle.id}
                  className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="h-5 w-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {vehicle.license_plate}
                        </h3>
                        {getStatusBadge(vehicle.status)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : vehicle.vehicle_type}
                        {vehicle.year && ` • ${vehicle.year}`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(vehicle)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Bearbeiten"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(vehicle.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Inspection Warning */}
                    {isInspectionOverdue && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-red-900">
                            Hauptuntersuchung überfällig!
                          </div>
                          <div className="text-xs text-red-700">
                            Fällig seit {Math.abs(daysUntilInspection)} Tagen
                          </div>
                        </div>
                      </div>
                    )}
                    {isInspectionSoon && !isInspectionOverdue && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                        <Calendar className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-yellow-900">
                            Hauptuntersuchung steht bevor
                          </div>
                          <div className="text-xs text-yellow-700">
                            In {daysUntilInspection} Tagen fällig
                          </div>
                        </div>
                      </div>
                    )}
                    {!isInspectionSoon && !isInspectionOverdue && vehicle.next_inspection_date && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-green-900">
                            Nächste Hauptuntersuchung
                          </div>
                          <div className="text-xs text-green-700">
                            {formatDate(vehicle.next_inspection_date)} ({daysUntilInspection} Tage)
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Vehicle Details */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {vehicle.stressfrei_id && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Stressfrei ID:</span>
                          <span className="ml-1 font-medium font-mono">{vehicle.stressfrei_id}</span>
                        </div>
                      )}
                      {vehicle.max_load_capacity && (
                        <div>
                          <span className="text-gray-500">Ladekapazität:</span>
                          <span className="ml-1 font-medium">{vehicle.max_load_capacity} kg</span>
                        </div>
                      )}
                      {vehicle.max_volume && (
                        <div>
                          <span className="text-gray-500">Volumen:</span>
                          <span className="ml-1 font-medium">{vehicle.max_volume} m³</span>
                        </div>
                      )}
                      {vehicle.mileage && (
                        <div>
                          <span className="text-gray-500">Kilometerstand:</span>
                          <span className="ml-1 font-medium">{vehicle.mileage.toLocaleString('de-DE')} km</span>
                        </div>
                      )}
                      {vehicle.fuel_type && (
                        <div>
                          <span className="text-gray-500">Kraftstoff:</span>
                          <span className="ml-1 font-medium">{vehicle.fuel_type}</span>
                        </div>
                      )}
                      {vehicle.insurance_expiry && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Versicherung läuft ab:</span>
                          <span className="ml-1 font-medium">{formatDate(vehicle.insurance_expiry)}</span>
                        </div>
                      )}
                      {vehicle.length && vehicle.width && vehicle.height && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Abmessungen:</span>
                          <span className="ml-1 font-medium">
                            {vehicle.length}m × {vehicle.width}m × {vehicle.height}m
                          </span>
                        </div>
                      )}
                    </div>

                    {vehicle.notes && (
                      <div className="pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-500 mb-1">Notizen:</div>
                        <div className="text-sm text-gray-700">{vehicle.notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default FleetManagement;

